import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Close,
  EditLocation,
  LocalHospital,
  MyLocation,
  Notes,
  PersonAdd,
  PersonRemove,
  ReportProblem,
} from '@mui/icons-material'
import API from '../../../../services/API'
import { ControlRoomContext } from './ControlPage'
import EmployeeSelector from '../../../events/EmployeeSelector'
import IncidentLocationPicker from './IncidentLocationPicker'
import OffDutyCrewDialog from './OffDutyCrewDialog'
import ForumIcon from '@mui/icons-material/Forum'
import {
  RESOURCE_ICONS,
  ResourceIcon,
  resolveResourceIconKey,
} from './resourceIcons'

const statusStyles = {
  Available: { bg: '#00964C', light: '#DCFCE7', text: '#166534' },
  'Off Duty': { bg: '#475569', light: '#E2E8F0', text: '#334155' },
  'Manager On Call': { bg: '#1F3D7C', light: '#DBEAFE', text: '#1E3A8A' },
  'Mobile & Available': { bg: '#00964C', light: '#DCFCE7', text: '#166534' },
  'C1 Only': { bg: '#00964C', light: '#DCFCE7', text: '#166534' },
  'C1 & C2 Only': { bg: '#00964C', light: '#DCFCE7', text: '#166534' },
  Break: { bg: '#1F3D7C', light: '#DBEAFE', text: '#1E3A8A' },
  Unavailable: { bg: '#85276A', light: '#FCE7F3', text: '#85276A' },
  Assigned: { bg: '#E32525', light: '#FEE2E2', text: '#991B1B' },
  Acknowledged: { bg: '#C2410C', light: '#FFEDD5', text: '#9A3412' },
  'On Route': { bg: '#9A3412', light: '#FFEDD5', text: '#9A3412' },
  'At Scene': { bg: '#CCA43B', light: '#FEF9C3', text: '#854D0E' },
  'On Scene': { bg: '#CCA43B', light: '#FEF9C3', text: '#854D0E' },
  'Leave Scene': { bg: '#CCA43B', light: '#FEF9C3', text: '#854D0E' },
  'At Hosp': { bg: '#CCA43B', light: '#FEF9C3', text: '#854D0E' },
  Handover: { bg: '#CCA43B', light: '#FEF9C3', text: '#854D0E' },
}

const trackingChipStyles = {
  Approved: { bg: '#DCFCE7', color: '#166534', label: 'Sharing location' },
  Pending: { bg: '#FEF9C3', color: '#854D0E', label: 'Request pending' },
  Declined: { bg: '#FEE2E2', color: '#991B1B', label: 'Declined sharing' },
  Expired: { bg: '#FFEDD5', color: '#9A3412', label: 'Sharing expired' },
}

const formatTime = (value) =>
  value
    ? new Date(value).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

const Section = ({ title, action, children }) => (
  <Box
    sx={{
      borderRadius: 4,
      border: '1px solid #E2E8F0',
      backgroundColor: '#FFFFFF',
      overflow: 'hidden',
    }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        px: 2,
        py: 1.5,
        backgroundColor: '#F8FAFC',
        borderBottom: '1px solid #E2E8F0',
      }}
    >
      <Typography fontWeight={900} color="#242F40">
        {title}
      </Typography>
      {action}
    </Stack>

    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
)

const eventMapCoordinatesEqual = (left, right) =>
  Array.isArray(left) &&
  Array.isArray(right) &&
  left.length === 2 &&
  right.length === 2 &&
  Number(left[0]) === Number(right[0]) &&
  Number(left[1]) === Number(right[1])

const ResourceDrawer = ({
  selectedResource,
  setSelectedResource,
  controlRoomID,
  onOpenChat,
}) => {
  const {
    setSelectedIncident,
    activeIncidents,
    eventMap,
    // Viewers get the drawer read-only: details, crew, chat history — no
    // status/crew/detail mutations. Server enforces the same tiering.
    canDispatch = true,
  } = useContext(ControlRoomContext)

  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null)
  const [showStaffSelector, setShowStaffSelector] = useState(false)
  const [selectedStaffMember, setSelectedStaffMember] = useState(null)
  const [actionError, setActionError] = useState('')
  const [note, setNote] = useState('')
  const [breakDialogOpen, setBreakDialogOpen] = useState(false)
  const [breakDuration, setBreakDuration] = useState(30)
  const [openLocationEditor, setOpenLocationEditor] = useState(false)
  const [locationEditingUnlocked, setLocationEditingUnlocked] = useState(false)
  const [eventMapCoordinates, setEventMapCoordinates] = useState(null)
  const [resourceName, setResourceName] = useState('')
  const [resourceNameSaving, setResourceNameSaving] = useState(false)
  const [resourceNameError, setResourceNameError] = useState('')

  const statusStyle = statusStyles[selectedResource?.status] || {
    bg: '#268EBB',
    light: '#E6F7F9',
    text: '#1E7F88',
  }

  useEffect(() => {
    setNote(selectedResource?.note ?? '')
  }, [selectedResource?.id, selectedResource?.note])

  useEffect(() => {
    setResourceName(selectedResource?.callSign ?? '')
    setResourceNameError('')
    setResourceNameSaving(false)
  }, [selectedResource?.id, selectedResource?.callSign])

  useEffect(() => {
    setEventMapCoordinates(selectedResource?.eventMapCoordinates ?? null)
    setLocationEditingUnlocked(false)
    setOpenLocationEditor(false)
  }, [selectedResource?.id, selectedResource?.eventMapCoordinates])

  const usesManualEventMap = !!eventMap?.s3key && !eventMap?.drawnToScale

  // Mirrors server resourceStatusRules.js: an Off Duty resource with nobody
  // assigned can't be moved to any other status — the options stay visible
  // but disabled, and the server 409s if the guard is bypassed.
  const offDutyWithoutCrew =
    selectedResource?.status === 'Off Duty' &&
    !(selectedResource?.users?.length > 0)

  const updateResource = (
    changes,
    { closeAfter = false, throwErrors = false } = {},
  ) => {
    if (!selectedResource?.id) return Promise.resolve()

    return API.post('/commandcontrol/updateresource', {
      controlRoomID,
      resourceID: selectedResource.id,
      changes,
    })
      .then((res) => {
        if (res.status === 200) {
          setActionError('')
          if (closeAfter) {
            handleClose()
          } else {
            setSelectedResource((prev) => ({
              ...prev,
              ...changes,
              ...(res.data?.resource ?? {}),
            }))
          }

          setStatusMenuAnchor(null)
          setBreakDialogOpen(false)
        } else {
          // API never rejects on 4xx — conflict guards (e.g. crew still on
          // an active resource elsewhere) land here with a message
          setActionError(
            res.data?.error || 'Could not update the resource. Try again.',
          )
          setStatusMenuAnchor(null)
          setBreakDialogOpen(false)
        }
        return res
      })
      .catch((e) => {
        console.log(e)
        if (throwErrors) throw e
      })
  }

  const saveResourceName = () => {
    const trimmed = resourceName.trim()

    if (!trimmed) {
      setResourceNameError('Enter a resource name.')
      return
    }

    if (trimmed === (selectedResource?.callSign ?? '')) return

    setResourceNameSaving(true)
    setResourceNameError('')

    updateResource({ callSign: trimmed }, { throwErrors: true })
      .then((res) => {
        if (res?.status === 200) {
          setResourceName(res.data?.resource?.callSign ?? trimmed.toUpperCase())
        }
      })
      .catch((e) => {
        setResourceNameError(
          e?.response?.data?.error || 'Could not update the resource name.',
        )
      })
      .finally(() => setResourceNameSaving(false))
  }

  const handleStatusSelect = (status) => {
    if (status === 'Break') {
      setBreakDuration(30)
      setStatusMenuAnchor(null)
      setBreakDialogOpen(true)
      return
    }

    updateResource({ status }, { closeAfter: true })
  }

  const [offDutyPrompt, setOffDutyPrompt] = useState(null)
  const [checkingDuty, setCheckingDuty] = useState(false)

  const appendUserLocally = (staffMember) => {
    setSelectedResource((prev) => ({
      ...prev,
      users: [
        ...(prev.users || []),
        {
          name: `${staffMember.firstName} ${staffMember.lastName}`,
          id: staffMember._id,
          clinicalRole:
            staffMember.employee?.clinicalRole ??
            staffMember.subcontractor?.clinicalRole,
          isSubcontractor: !!staffMember.subcontractor,
        },
      ],
    }))
  }

  // The plain add — crews the person without touching their shifts. Returns
  // {ok, error} so the off-duty dialog can surface failures in place.
  const commitAddUser = (staffMember) =>
    API.post('/commandcontrol/assignusertoresource', {
      controlRoomID,
      resourceID: selectedResource.id,
      user: staffMember._id,
    }).then((res) => {
      if (res.status === 200) {
        setActionError('')
        setShowStaffSelector(false)
        appendUserLocally(staffMember)
        setSelectedStaffMember(null)
        return { ok: true }
      }
      // e.g. already crewed on a live resource in another control room
      return {
        ok: false,
        error: res.data?.error || 'Could not add this person to the resource.',
      }
    })

  // The "create a shift for them" path from the off-duty dialog
  const createShiftAndAdd = (staffMember, { start, end }) =>
    API.post('/commandcontrol/assign-crew-with-shift', {
      controlRoomID,
      resourceID: selectedResource.id,
      user: staffMember._id,
      start,
      end,
    }).then((res) => {
      if (res.status === 200) {
        setActionError('')
        setShowStaffSelector(false)
        appendUserLocally(staffMember)
        setSelectedStaffMember(null)
        return { ok: true }
      }
      if (res.data?.errorCode === 'shiftAssignmentBlocked') {
        return {
          ok: false,
          error:
            (res.data.issues || [])
              .map((assignmentIssue) => assignmentIssue.message)
              .join(' ') || 'This shift clashes with an existing assignment.',
        }
      }
      return {
        ok: false,
        error: res.data?.error || 'Could not create the shift.',
      }
    })

  const addNewUser = () => {
    if (!selectedStaffMember || checkingDuty) return

    // Selector options are user accounts for employees AND subcontractors —
    // _id is always the user account id
    if (
      selectedResource?.users?.some((x) => x?.id === selectedStaffMember?._id)
    ) {
      setSelectedStaffMember(null)
      setShowStaffSelector(false)
      return
    }

    // Off-duty people can still be crewed, but the dispatcher should do it
    // knowingly — check first and warn with their booked shifts.
    setCheckingDuty(true)
    API.get(`/commandcontrol/crew-duty-status?user=${selectedStaffMember._id}`)
      .then((res) => {
        if (res.status === 200 && res.data?.onDuty) {
          return commitAddUser(selectedStaffMember).then((result) => {
            if (!result.ok) setActionError(result.error)
          })
        }
        if (res.status === 200) {
          setOffDutyPrompt({
            staffMember: selectedStaffMember,
            status: res.data,
          })
          return undefined
        }
        setActionError(
          res.data?.error || 'Could not add this person to the resource.',
        )
        return undefined
      })
      .catch((e) => console.log(e))
      .finally(() => setCheckingDuty(false))
  }

  const [requestingTrackingFor, setRequestingTrackingFor] = useState(null)

  // Ask (or re-ask) a crew member to share their location — used after an
  // accidental decline, an expired approval, or to nudge an unanswered request
  const requestTracking = (user) => {
    if (!selectedResource?.id || requestingTrackingFor) return
    setRequestingTrackingFor(user.id)
    API.post(
      `/commandcontrol/resources/${selectedResource.id}/request-tracking`,
      {
        controlRoomID,
        userID: user.id,
      },
    )
      .then((res) => {
        if (res.status === 200) {
          setSelectedResource((prev) => ({
            ...prev,
            users: (prev?.users ?? []).map((existing) =>
              existing.id === user.id
                ? {
                    ...existing,
                    tracking: {
                      ...(existing.tracking ?? {}),
                      status: 'Pending',
                      requestedAt: new Date().toISOString(),
                    },
                  }
                : existing,
            ),
          }))
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setRequestingTrackingFor(null))
  }

  const removeUserFromResource = ({ user, resource }) => {
    API.post('/commandcontrol/removeuserfromresource', {
      controlRoomID,
      resourceID: resource.id,
      userID: user.id,
      userName: user.name,
      callSign: resource.callSign,
    }).then((res) => {
      if (res.status === 200) {
        setSelectedResource((prev) => ({
          ...prev,
          users: (prev?.users ?? []).filter((x) => x.id !== user.id),
        }))
      }
    })
  }

  const saveNote = () => {
    const trimmed = note ?? ''
    if (trimmed === (selectedResource?.note ?? '')) return
    updateResource({ note: trimmed })
  }

  const openManualLocationEditor = () => {
    setEventMapCoordinates(selectedResource?.eventMapCoordinates ?? null)
    setLocationEditingUnlocked(false)
    setOpenLocationEditor(true)
  }

  const closeManualLocationEditor = () => {
    if (
      selectedResource?.id &&
      eventMapCoordinates?.length === 2 &&
      !eventMapCoordinatesEqual(
        eventMapCoordinates,
        selectedResource?.eventMapCoordinates,
      )
    ) {
      updateResource({ eventMapCoordinates })
    }

    setOpenLocationEditor(false)
    setLocationEditingUnlocked(false)
  }

  const handleRemoveResourceFromControlRoom = () => {
    API.post('/commandcontrol/removeresourcefromcontrolroom', {
      controlRoomID,
      resourceID: selectedResource?.id,
    }).then((res) => {
      if (res.status === 200) {
        setSelectedResource(null)
      }
    })
  }

  const handleClose = () => {
    setSelectedResource(null)
    setNote('')
    setShowStaffSelector(false)
    setSelectedStaffMember(null)
    setActionError('')
  }

  return (
    <Drawer
      open={!!selectedResource}
      onClose={handleClose}
      anchor="left"
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          maxWidth: '100vw',
          backgroundColor: '#F8FAFC',
        },
      }}
    >
      <Box
        sx={{
          color: 'white',
          p: 2.5,
          background:
            'linear-gradient(135deg, #1A2230 0%, #242F40 55%, #239AA4 150%)',
        }}
      >
        <Stack direction="row" justifyContent="space-between" gap={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
              }}
            >
              <LocalHospital />
            </Box>

            <Stack>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                Resource
              </Typography>
              <Typography variant="h5" fontWeight={900} color="white">
                {selectedResource?.callSign ?? ''}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.78 }} color="white">
                {selectedResource?.type ?? 'Other'}
              </Typography>
            </Stack>
          </Stack>

          <IconButton
            onClick={handleClose}
            sx={{
              color: 'white',
              alignSelf: 'flex-start',
              backgroundColor: 'rgba(255,255,255,0.10)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
            }}
          >
            <Close />
          </IconButton>
        </Stack>
      </Box>

      <Stack spacing={2} sx={{ p: 2 }}>
        {actionError && (
          <Alert
            severity="error"
            onClose={() => setActionError('')}
            sx={{ borderRadius: 3 }}
          >
            {actionError}
          </Alert>
        )}

        <Button
          onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
          variant="contained"
          fullWidth
          disabled={!!selectedResource?.assignedIncident || !canDispatch}
          sx={{
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 900,
            color: 'white',
            backgroundColor: statusStyle.bg,
            boxShadow: `0 12px 28px ${statusStyle.bg}44`,
            '&:hover': {
              backgroundColor: statusStyle.bg,
              filter: 'brightness(0.95)',
            },
          }}
        >
          {selectedResource?.status || 'Unknown'}
        </Button>

        {canDispatch && selectedResource?.status === 'Break' && (
          <Button
            onClick={() => updateResource({ status: 'Available' }, { closeAfter: true })}
            variant="contained"
            fullWidth
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#2563EB',
              '&:hover': { backgroundColor: '#1D4ED8' },
              ...(selectedResource?.breakUntil &&
                new Date(selectedResource.breakUntil).getTime() <= Date.now() && {
                  animation: 'endBreakPulse 1.4s ease-in-out infinite',
                  '@keyframes endBreakPulse': {
                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(37,99,235,0.45)' },
                    '50%': { boxShadow: '0 0 0 8px rgba(37,99,235,0.12)' },
                  },
                }),
            }}
          >
            End break
          </Button>
        )}

        <Menu
          anchorEl={statusMenuAnchor}
          open={!!statusMenuAnchor}
          onClose={() => setStatusMenuAnchor(null)}
        >
          {offDutyWithoutCrew && (
            // Disabled so MenuList skips it for focus; opacity restored so the
            // hint itself reads at full strength.
            <MenuItem
              disabled
              sx={{
                '&.Mui-disabled': { opacity: 1 },
                whiteSpace: 'normal',
                maxWidth: 280,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Nobody is assigned to this resource. Add a crew member before
                changing its status.
              </Typography>
            </MenuItem>
          )}
          {[
            'Available',
            'Off Duty',
            'Break',
            'Unavailable',
            'Manager On Call',
            'Mobile & Available',
            'C1 Only',
            'C1 & C2 Only',
          ].map((x) => (
            <MenuItem
              key={x}
              onClick={() => handleStatusSelect(x)}
              disabled={offDutyWithoutCrew && x !== 'Off Duty'}
            >
              {x}
            </MenuItem>
          ))}
        </Menu>

        <Dialog
          open={breakDialogOpen}
          onClose={() => setBreakDialogOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Start Break</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Confirm how long {selectedResource?.callSign} should remain on
                break.
              </Typography>
              <TextField
                label="Break length"
                type="number"
                value={breakDuration}
                onChange={(e) => setBreakDuration(e.target.value)}
                inputProps={{ min: 1, max: 1440, step: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">mins</InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setBreakDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() =>
                updateResource(
                  {
                    status: 'Break',
                    breakDurationMinutes: Number(breakDuration) || 30,
                  },
                  { closeAfter: true },
                )
              }
            >
              Start Break
            </Button>
          </DialogActions>
        </Dialog>

        {selectedResource?.assignedIncident && (
          <Button
            onClick={() => {
              setNote('')
              setSelectedIncident(
                activeIncidents.find(
                  (x) => x.id === selectedResource?.assignedIncident,
                ),
              )
              setSelectedResource(null)
            }}
            fullWidth
            variant="contained"
            startIcon={<ReportProblem />}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#E32525',
              '&:hover': { backgroundColor: '#B91C1C' },
            }}
          >
            View Assigned Incident
          </Button>
        )}

        {canDispatch && selectedResource?.status === 'Off Duty' && (
          <Alert
            severity="warning"
            sx={{
              borderRadius: 3,
              backgroundColor: '#FFF7ED',
              color: '#9A3412',
              border: '1px solid #FED7AA',
            }}
            action={
              <Button
                onClick={handleRemoveResourceFromControlRoom}
                size="small"
                color="inherit"
                sx={{ fontWeight: 900 }}
              >
                Remove
              </Button>
            }
          >
            {offDutyWithoutCrew
              ? 'This resource is off duty with nobody assigned. Add a crew member below before changing its status, or remove it from the control room.'
              : 'This resource is off duty and can be removed from the control room.'}
          </Alert>
        )}

        {usesManualEventMap && (
          <Section title="Event Map Location">
            <Stack spacing={1.25}>
              <Typography variant="body2" color="text.secondary">
                {selectedResource?.eventMapCoordinates?.length === 2
                  ? `Position: ${selectedResource.eventMapCoordinates
                      .map((value) => `${Math.round(value * 100)}%`)
                      .join(', ')}`
                  : 'No manual event map position recorded.'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<EditLocation />}
                onClick={openManualLocationEditor}
                disabled={!canDispatch}
                sx={{
                  alignSelf: 'flex-start',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 900,
                  backgroundColor: '#239AA4',
                }}
              >
                Edit location
              </Button>
            </Stack>
          </Section>
        )}

        <Dialog
          open={openLocationEditor}
          onClose={closeManualLocationEditor}
          fullWidth
          maxWidth="xl"
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <EditLocation sx={{ color: '#239AA4' }} />
                <Box>
                  <Typography fontWeight={900}>Resource Location</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedResource?.callSign ?? 'Resource'}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={closeManualLocationEditor}>
                <Close />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 2, backgroundColor: '#F8FAFC' }}>
            <IncidentLocationPicker
              coordinates=""
              setCoordinates={() => {}}
              eventMapCoordinates={eventMapCoordinates}
              setEventMapCoordinates={setEventMapCoordinates}
              eventMap={eventMap}
              initialMapView="event"
              height="min(72vh, 720px)"
              locked={!locationEditingUnlocked}
              onUnlock={() => setLocationEditingUnlocked(true)}
            />
          </DialogContent>
        </Dialog>

        <Section
          title="Messages"
          action={
            <IconButton
              size="small"
              onClick={() => onOpenChat?.(selectedResource)}
              sx={{
                backgroundColor: '#E6F7F9',
                color: '#1E7F88',
                '&:hover': { backgroundColor: '#D7F1F4' },
              }}
            >
              <ForumIcon fontSize="small" />
            </IconButton>
          }
        >
          <Button
            variant="outlined"
            fullWidth
            startIcon={<ForumIcon />}
            onClick={() => onOpenChat?.(selectedResource)}
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >
            Open chat with {selectedResource?.callSign || 'this resource'}
          </Button>
        </Section>

        <Section title="Resource Details">
          <Stack spacing={1.5}>
            {resourceNameError && (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {resourceNameError}
              </Alert>
            )}

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Resource name"
                value={resourceName}
                disabled={!canDispatch}
                onChange={(e) => {
                  setResourceName(e.target.value.toUpperCase())
                  setResourceNameError('')
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    saveResourceName()
                  }
                }}
                fullWidth
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalHospital fontSize="small" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 3 },
                }}
              />
              <Button
                variant="contained"
                onClick={saveResourceName}
                disabled={
                  !canDispatch ||
                  resourceNameSaving ||
                  !resourceName.trim() ||
                  resourceName.trim() === (selectedResource?.callSign ?? '')
                }
                sx={{
                  minHeight: 56,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 900,
                  backgroundColor: '#239AA4',
                }}
              >
                Save
              </Button>
            </Stack>

            <Select
              fullWidth
              value={selectedResource?.type ?? 'Other'}
              disabled={!canDispatch}
              onChange={(e) => updateResource({ type: e.target.value })}
              sx={{ borderRadius: 3, backgroundColor: 'white' }}
            >
              {[
                'Ambulance',
                'Medical Centre',
                'RRV',
                'Response Team',
                'Officer',
                'Other',
              ].map((x) => (
                <MenuItem key={x} value={x}>
                  {x}
                </MenuItem>
              ))}
            </Select>

            <Stack spacing={0.75}>
              <Typography
                variant="caption"
                sx={{ color: '#64748B', fontWeight: 700 }}
              >
                Map icon
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                {RESOURCE_ICONS.map((option) => {
                  const active =
                    resolveResourceIconKey(selectedResource) === option.key
                  return (
                    <Tooltip key={option.key} title={option.label}>
                      <Box
                        onClick={() => {
                          if (!canDispatch || active) return
                          updateResource({ icon: option.key })
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: canDispatch ? 'pointer' : 'default',
                          backgroundColor: active ? '#0F172A' : 'white',
                          border: `1px solid ${active ? '#0F172A' : '#CBD5E1'}`,
                          color: active ? 'white' : '#334155',
                          transition: 'all 0.15s ease',
                          '&:hover': canDispatch
                            ? { borderColor: '#0F172A' }
                            : undefined,
                        }}
                      >
                        <ResourceIcon
                          iconKey={option.key}
                          size={20}
                          color="currentColor"
                        />
                      </Box>
                    </Tooltip>
                  )
                })}
              </Stack>
            </Stack>

            <TextField
              label="Resource note"
              value={note ?? ''}
              disabled={!canDispatch}
              onChange={(e) => setNote(e.target.value)}
              onBlur={saveNote}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  e.currentTarget.blur()
                }
              }}
              fullWidth
              multiline
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      mr: 1,
                      color: '#64748B',
                      display: 'flex',
                    }}
                  >
                    <Notes fontSize="small" />
                  </Box>
                ),
                sx: { borderRadius: 3 },
              }}
            />
          </Stack>
        </Section>

        <Section
          title="Assigned Staff"
          action={
            canDispatch ? (
              <IconButton
                size="small"
                onClick={() => setShowStaffSelector((prev) => !prev)}
                sx={{
                  backgroundColor: '#E6F7F9',
                  color: '#1E7F88',
                  '&:hover': { backgroundColor: '#D7F1F4' },
                }}
              >
                <PersonAdd />
              </IconButton>
            ) : undefined
          }
        >
          <Stack spacing={1.5}>
            {canDispatch && showStaffSelector && (
              <Stack
                component="form"
                onSubmit={(e) => {
                  e.preventDefault()
                  addNewUser()
                }}
                spacing={1}
              >
                <EmployeeSelector
                  value={selectedStaffMember}
                  onChange={(e, value) => setSelectedStaffMember(value)}
                  shrinkLabel={!!selectedStaffMember}
                  includeSubcontractor
                />

                <Button
                  fullWidth
                  disabled={!selectedStaffMember || checkingDuty}
                  variant="contained"
                  type="submit"
                  sx={{
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 900,
                    backgroundColor: '#239AA4',
                    '&:hover': { backgroundColor: '#1E7F88' },
                  }}
                >
                  Add Staff Member
                </Button>

                <Divider />
              </Stack>
            )}

            {(selectedResource?.users ?? []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No staff assigned to this resource.
              </Typography>
            ) : (
              (selectedResource?.users ?? []).map((user) => (
                <Box
                  key={user.id}
                  sx={{
                    p: 1.25,
                    borderRadius: 3,
                    backgroundColor: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 2,
                          backgroundColor: '#239AA4',
                        }}
                      >
                        {user.name?.[0]}
                      </Avatar>

                      <Stack>
                        <Typography fontWeight={800} color="#242F40">
                          {user.name}
                        </Typography>
                        <Stack
                          direction="row"
                          gap={0.5}
                          flexWrap="wrap"
                          sx={{ mt: 0.5 }}
                        >
                          {user.clinicalRole && (
                            <Chip
                              size="small"
                              label={user.clinicalRole}
                              sx={{
                                borderRadius: 999,
                                backgroundColor: '#E6F7F9',
                                color: '#1E7F88',
                                fontWeight: 800,
                              }}
                            />
                          )}
                          {user.tracking && (
                            <Tooltip
                              title={
                                user.tracking.status === 'Approved' &&
                                user.tracking.until
                                  ? `Approved until ${formatTime(user.tracking.until)} (or clock-out)`
                                  : user.tracking.status === 'Pending'
                                    ? 'Waiting for the crew member to respond on their device'
                                    : 'Location is not being shared'
                              }
                            >
                              <Chip
                                size="small"
                                label={
                                  trackingChipStyles[user.tracking.status]
                                    ?.label ?? user.tracking.status
                                }
                                sx={{
                                  borderRadius: 999,
                                  backgroundColor:
                                    trackingChipStyles[user.tracking.status]
                                      ?.bg ?? '#F1F5F9',
                                  color:
                                    trackingChipStyles[user.tracking.status]
                                      ?.color ?? '#475569',
                                  fontWeight: 800,
                                }}
                              />
                            </Tooltip>
                          )}
                          {user.shiftEnd && (
                            <Chip
                              size="small"
                              label={`Shift ends ${formatTime(user.shiftEnd)}`}
                              sx={{
                                borderRadius: 999,
                                backgroundColor:
                                  new Date(user.shiftEnd).getTime() -
                                    Date.now() <=
                                  60 * 60 * 1000
                                    ? '#FEF9C3'
                                    : '#F1F5F9',
                                color:
                                  new Date(user.shiftEnd).getTime() -
                                    Date.now() <=
                                  60 * 60 * 1000
                                    ? '#854D0E'
                                    : '#475569',
                                fontWeight: 800,
                              }}
                            />
                          )}
                        </Stack>
                        {canDispatch &&
                          user.tracking &&
                          user.tracking.status !== 'Approved' && (
                            <Button
                              size="small"
                              startIcon={<MyLocation />}
                              disabled={requestingTrackingFor === user.id}
                              onClick={() => requestTracking(user)}
                              sx={{
                                mt: 0.75,
                                alignSelf: 'flex-start',
                                borderRadius: 999,
                                textTransform: 'none',
                                fontWeight: 800,
                                color: '#1E7F88',
                                backgroundColor: '#E6F7F9',
                                px: 1.5,
                                '&:hover': { backgroundColor: '#D7F1F4' },
                              }}
                            >
                              {user.tracking.status === 'Pending'
                                ? 'Re-send location request'
                                : 'Request location again'}
                            </Button>
                          )}
                      </Stack>
                    </Stack>

                    {canDispatch && (
                      <IconButton
                        onClick={() =>
                          removeUserFromResource({
                            user,
                            resource: selectedResource,
                          })
                        }
                        sx={{
                          color: '#E32525',
                          '&:hover': { backgroundColor: '#FEE2E2' },
                        }}
                      >
                        <PersonRemove />
                      </IconButton>
                    )}
                  </Stack>
                </Box>
              ))
            )}
          </Stack>
        </Section>
      </Stack>

      <OffDutyCrewDialog
        open={!!offDutyPrompt}
        onClose={() => setOffDutyPrompt(null)}
        staffName={
          offDutyPrompt
            ? `${offDutyPrompt.staffMember.firstName} ${offDutyPrompt.staffMember.lastName}`
            : ''
        }
        status={offDutyPrompt?.status}
        onAddAndWait={() => commitAddUser(offDutyPrompt.staffMember)}
        onCreateShift={(times) =>
          createShiftAndAdd(offDutyPrompt.staffMember, times)
        }
      />
    </Drawer>
  )
}

export default ResourceDrawer
